/**
 * Thin fetch wrapper for the PHP API. Same-origin, cookies always sent,
 * CSRF token attached to every mutation and refreshed on 419.
 */

const BASE = '/api'

let csrfToken = null
let sessionRequest = null

export class ApiError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function parse(response) {
  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    throw new ApiError(payload?.error?.message ?? `Error ${response.status}`, response.status)
  }

  return payload
}

export async function apiGet(path) {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })

  // The service worker flags responses it served from its own cache.
  const payload = await parse(response)

  if (payload !== null && typeof payload === 'object') {
    payload.__offline = response.headers.get('X-Differlx-Offline') === '1'
  }

  return payload
}

/**
 * Fetches the session, which also issues the CSRF token this client uses.
 *
 * Concurrent callers share one request: two parallel calls on a cold start
 * would create two PHP sessions, and the token from one is invalid for the
 * other.
 */
export async function loadSession() {
  sessionRequest ??= apiGet('/auth/session')
    .then((session) => {
      csrfToken = session.csrf_token

      return session
    })
    .finally(() => {
      sessionRequest = null
    })

  return sessionRequest
}

/**
 * Mutating request with the CSRF header. Pass a FormData for uploads — the
 * browser must set its own multipart boundary, so Content-Type is left alone.
 */
async function mutate(method, path, body = {}, { retryOnCsrf = true } = {}) {
  if (csrfToken === null) {
    await loadSession()
  }

  const isForm = body instanceof FormData
  const headers = { Accept: 'application/json', 'X-CSRF-Token': csrfToken }

  if (!isForm) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    body: isForm ? body : JSON.stringify(body),
  })

  // A stale token means the session rotated; take a fresh one and retry once.
  if (response.status === 419 && retryOnCsrf) {
    await loadSession()

    return mutate(method, path, body, { retryOnCsrf: false })
  }

  const payload = await parse(response)

  // Login endpoints hand back the rotated token.
  if (typeof payload?.csrf_token === 'string') {
    csrfToken = payload.csrf_token
  }

  return payload
}

export function apiPost(path, body, options) {
  return mutate('POST', path, body, options)
}

export function apiPut(path, body, options) {
  return mutate('PUT', path, body, options)
}

export function apiDelete(path, body, options) {
  return mutate('DELETE', path, body, options)
}
